/**
 * Plan 工作台页签表（PR0 新写）：菜单各项对应的 hash、标题、一句话、期次。
 * 菜单树唯一出处是概要设计《04 后台页面》§3.2；侧栏分组在平台外壳 `routes/__root.tsx` 的 `plan-*` 分组。
 * 后台页面对齐批 2（10-19）：删「固定用例」「运行观测」；新一级「评测配置」（4 项占位，批 3 ／ 批 4 上线）、
 * 「知识管理」（政策知识＝原「知识」页，另 3 项规划占位）；改名 信号识别 ／ 候选召回 ／ 事项看板 ／ 信息流向 ／ 方法沉淀。
 * 占位页只有页头 ＋ 置灰说明（plan-page-design ref 03 §1），说明文字由 `placeholderNote` 给。
 */

export type PlanTab =
	| "overview"
	| "playground"
	| "eval"
	| "recall-eval"
	| "history"
	| "eval-records"
	| "eval-reports"
	| "stats"
	| "eval-bank"
	| "eval-cx"
	| "eval-judges"
	| "eval-rules"
	| "tags"
	| "signal"
	| "recall"
	| "tools"
	| "guidance"
	| "business-params"
	| "planner"
	| "guards"
	| "field-contract"
	| "info-flow"
	| "prompts"
	| "config-versions"
	| "method-skills"
	| "knowledge"
	| "knowledge-product"
	| "knowledge-table"
	| "knowledge-gap";

/** 占位页的期次：P1 ／ P2（原分期）、批 3 ／ 批 4（后台页面对齐专项的批次）、规划（还没排期、后端无数据源） */
export type PlanPhase = "P1" | "P2" | "批 3" | "批 4" | "规划";

export type PlanTabMeta = {
	title: string;
	desc: string;
	/** 后续期占位页才有 */
	placeholder?: PlanPhase;
};

/** 占位页的置灰说明（链路总览不可点节点的悬停提示也用它） */
export function placeholderNote(phase: PlanPhase): string {
	if (phase === "规划") return "规划中，暂未排期";
	if (phase === "P1" || phase === "P2") return `${phase} 起提供`;
	return `${phase} 上线`;
}

export const PLAN_TABS: Record<PlanTab, PlanTabMeta> = {
	overview: {
		title: "链路总览",
		desc: "plan 要解决什么问题、一轮怎么转、各页面在链路哪里、页面之间谁读谁；点节点或卡片跳到对应页面。",
	},
	playground: {
		title: "人工提问",
		desc: "人工输入一句话，看一轮完整的 plan 轨迹。",
	},
	eval: {
		title: "模拟场景",
		desc: "机器顾客 × 判分 × pass^k。",
	},
	"recall-eval": {
		title: "召回评测",
		desc: "召回这一层单独评：逐轮金标单层回放，看工具与指南的召回率、精准率，坏例下钻到候选表。",
	},
	history: {
		title: "运行记录",
		desc: "plan 的单轮和会话记录；点一条打开运行轨迹抽屉。",
	},
	"eval-records": {
		title: "评测记录",
		desc: "plan 评测批次历史。",
	},
	"eval-reports": {
		title: "评测报告",
		desc: "全量评测的人工分析文档（不是单批次统计，单批结果在模拟场景与评测记录）：分场景、分用例的效果与失败归因。点一行看版本记录和全文；与仓库同源，变更走 PR。",
	},
	stats: {
		title: "指标统计",
		desc: "评测和人工提问的真实耗时：整批等多久、慢在哪一段、单轮多快。整批墙钟 ≠ 试跑耗时之和（有并发）；中位数会藏长尾，所以同时给 p90 和最长；批次排队串行，一批跑完才开下一批。",
	},
	"eval-bank": {
		title: "题库快照",
		desc: "评测用的题库快照：场景（每题演什么）、金标、诉求映射、模拟客户（所有题共用的怎么演）。只读，改动走 Loop 侧再导出新快照。",
	},
	"eval-cx": {
		title: "体验评分",
		desc: "CX 体验判官：1～5 分口径、七条理由、封顶与待复核规则、判官提示词。只作观测，不计入通过率。",
	},
	"eval-judges": {
		title: "判官提示",
		desc: "每个判官判什么、结论进不进通过率、所用模型、给什么不给什么、提示词全文。只读，提示词和模型随题库快照，改动走 Loop 侧再导出。",
	},
	"eval-rules": {
		title: "判分规则",
		desc: "评测怎么判过败、怎么算分母：三维乘积与安全否决、事项比对、规定次数与 pass^k、未判定、轮数上限、花费闸、观测列。只读，判分代码直接读这张表，改口径走 PR。",
	},
	tags: {
		title: "标签字典",
		desc: "应答链路用到的标签：分维度、最多两级，每个维度标明来源（平台内置 ／ 系统计算 ／ 客户自定义）、取值从哪读、被谁引用，另附字典体检。只读，本期由平台维护，改动走版本。",
	},
	signal: {
		title: "信号识别",
		desc: "一轮里的第 ① 步：读懂客户这句话——拆诉求、打意图、抽业务对象与信息项、判情绪与风险、判接续；规则层先抽确定事实，确认门守写操作。不选工具、不改事项。标签的定义统一在「标签字典」，这里按它取数。只读，改表与规则走 PR。",
	},
	recall: {
		title: "候选召回",
		desc: "每件诉求给全候选工具与指南：工具画像、候选生成（按说明判相关度、意图加分）与准入、数据状态、排序与呈现分层、指南召回、知识推送；画像改完保存为新版本，回归不降才放行。",
	},
	tools: {
		title: "工具能力",
		desc: "plan 在用的工具：契约、召回画像、返回字段、接入检查；可用演示数据试调用（写工具只预演）。只读，改工具走 PR。",
	},
	guidance: {
		title: "应答指南",
		desc: "线上每轮按条件注入 N2 决策 ／ N3 回复的指南：当什么条件、做什么、多强、给谁。只读，召回条件在候选召回页改。",
	},
	"business-params": {
		title: "业务参数",
		desc: "店铺政策的数值（退货窗口、到账天数、发货时限、服务时间……）、接入平台的口径（订单号怎么比对、状态值、归属字段……）和写操作默认值：每项的含义、当前值、被哪些运行规则读。只读，本期由平台维护，改动走版本。",
	},
	planner: {
		title: "事项看板",
		desc: "一轮里的第 ② 步：按诉求记账——建事项、并信息、撤销、拆单，产出本轮要推进的事项；不选工具、不调工具。只读，改字段与规则走 PR。",
	},
	guards: {
		title: "运行规则",
		desc: "运行时代码里的确定性规则：在哪个环节、做什么、能不能配、命中在哪看。只读，改规则走 PR。",
	},
	"field-contract": {
		title: "字段契约",
		desc: "工具返回的每个字段给哪些环节：事实表存、N2 决策看、N3 回复看、信号看、只给代码用；没登记的字段不会被用到并告警。只读，改契约走 PR。",
	},
	"info-flow": {
		title: "信息流向",
		desc: "大模型节点的每块输入从哪来、谁组装、什么时候给；组装出没登记的块会被丢掉并告警。只读，改登记走 PR。",
	},
	prompts: {
		title: "模型提示",
		desc: "改各节点提示词和模型，保存为新快照，下一轮生效。",
	},
	"config-versions": {
		title: "配置版本",
		desc: "plan 的配置每次保存成了哪个快照：谁、何时、改了什么、前后对比。",
	},
	"method-skills": {
		title: "方法沉淀",
		desc: "仓库里固化的 Plan 与通用方法 skill（归因、出报告、写业务包、方案评估）：说明、版本历史与全文。前端规范类 skill 不列在这里。只读，与仓库同源，变更走 PR；Loop 专用的在 Loop 页。",
	},
	knowledge: {
		title: "政策知识",
		desc: "店铺政策与常识条目：按类型看、筛；按相关性推给 Agent，也供知识检索工具查。只读，改内容走 PR。",
	},
	"knowledge-product": {
		title: "商品知识",
		desc: "商品目录、规格、库存等商品信息；现由商品类工具与种子数据供给，经字段契约给 N2 ／ N3。",
		placeholder: "规划",
	},
	"knowledge-table": {
		title: "表格知识",
		desc: "运费表、尺码表、时效表等结构化表，按条件查值。",
		placeholder: "规划",
	},
	"knowledge-gap": {
		title: "知识缺口",
		desc: "知识检索查空、触发「知识缺失」常备指南的会话，用来补知识。",
		placeholder: "规划",
	},
};

/**
 * 已上线真实内容的页（链路总览据此点亮节点，判定卡 10「节点随各页上线逐个点亮」）。各页升级 PR 把自己加进来：
 * PR3 人工提问、运行记录；PR6 模型提示、配置版本、评测记录；PR9 模拟场景、模式对比。
 */
export const PLAN_LIVE_TABS: ReadonlySet<PlanTab> = new Set<PlanTab>([
	"overview",
	"playground",
	"history",
	"prompts",
	"config-versions",
	"eval-records",
	"eval",
	"method-skills",
	"eval-reports",
	"recall",
	"tools",
	// 标签体系 v2 批 1：应答配置 › 标签字典
	"tags",
	// 后台对齐批 4：应答配置 › 信号识别、事项看板
	"signal",
	"planner",
	"recall-eval",
	"guards",
	"knowledge",
	"field-contract",
	"info-flow",
	// 后台页面对齐批 3 B 路：评测配置 › 题库快照、体验评分
	"eval-bank",
	"eval-cx",
	// 后台页面对齐批 4：评测配置 › 判官提示、判分规则
	"eval-judges",
	"eval-rules",
	"guidance",
	// 后台页面对齐批 5b：业务配置 › 业务参数
	"business-params",
	// 后台页面对齐批 4：数据分析 › 指标统计（耗时）
	"stats",
]);

export const PLAN_TAB_KEYS = Object.keys(PLAN_TABS) as PlanTab[];

/** 无 hash 或 hash 不认识时的默认落地页（判定卡 01：人工提问） */
export const PLAN_DEFAULT_TAB: PlanTab = "playground";

export function parsePlanTab(hash: string): PlanTab {
	const raw = hash.replace(/^#/, "");
	return (PLAN_TAB_KEYS as string[]).includes(raw) ? (raw as PlanTab) : PLAN_DEFAULT_TAB;
}
